import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Container from '@material-ui/core/Container';
import { getShippingTotal } from 'utils/priceHelpers';
import { Grid, Typography } from '@material-ui/core';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import IconButton from '@material-ui/core/IconButton';
import {
  setShippingMethod,
  calculatePrice,
  updateCart,
  updateCustomer,
} from 'store/actions/cart';
import GlobalOptions from './GlobalOptions';
import OptionsSection from 'components/OptionsSection';
import Tooltip from '@material-ui/core/Tooltip';
import clipboard from 'utils/clipboard';
import { money } from 'utils';
import { useAuth } from 'contexts/AuthContext';

const useStyles = makeStyles((theme) => ({
  btnxl: {
    height: '40px',
    margin: '20px 10px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 4, // Match the border radius of shipping options
    },
  },
}));

const Shipping = ({ history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cart = useSelector((s) => s.cart);
  const { globalOptions } = useSelector((s) => s.lab);
  const snack = useSnackbar();
  const { isAuthenticated, addressData, isProfileComplete, getAddressData } =
    useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [freshAddressData, setFreshAddressData] = useState(null);

  useEffect(() => {
    dispatch(calculatePrice());
  }, [cart.shippingMethod, cart.orderOptions]);
  
  useEffect(() => {
    // Pre-fetch fresh address data if user is authenticated and has complete profile
    // This eliminates the delay when clicking "Continue to Payment"
    if (isAuthenticated && isProfileComplete()) {
      setIsLoading(true);
      getAddressData()
        .then(data => {
          setFreshAddressData(data);
        })
        .catch(error => {
          console.warn('[Shipping] Failed to pre-fetch address data:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  const onChangeInput = (e) => {
    const { name, value } = e.target;
    dispatch(
      updateCart({
        [name]: value,
      })
    );
  };

  const onPaste = () => {
    clipboard((text) => {
      onChangeInput({
        target: {
          name: 'jobRef',
          value: text,
        },
      });
    });
  };

  const onNext = async () => {
    if (globalOptions.global_checkout_options?.length) {
      const isInvalid = globalOptions.global_checkout_options.some(
        (i) => i.required && !cart.orderOptions[i.id]
      );
      if (isInvalid) {
        return snack.enqueueSnackbar('Please select all the required options');
      }
    }

    // Check if user is authenticated, has complete profile, and selected non-dropship shipping
    const isNonDropship =
      cart.shippingMethod && cart.shippingMethod.shiptype !== 'dropship';

    if (isAuthenticated && isProfileComplete() && isNonDropship) {
      // Use pre-fetched fresh address data
      if (freshAddressData) {
        // Auto-populate customer info from user's profile and skip address page
        const customerData = {
          firstName: freshAddressData.firstName || '',
          lastName: freshAddressData.lastName || '',
          businessName: freshAddressData.businessName || '',
          phone: freshAddressData.phone || '',
          email: freshAddressData.email || '',
          address1: freshAddressData.address1 || '',
          address2: freshAddressData.address2 || '',
          city: freshAddressData.city || '',
          state: freshAddressData.state || '',
          zip: freshAddressData.zipCode || '',
          userId: freshAddressData.labAccountNumber || '',
          country: 'US', // Default to US
        };

        dispatch(updateCustomer(customerData));
        history.push('/checkout/pay');
      } else {
        // Fallback: if pre-fetched data isn't available, fetch it now
        setIsLoading(true);
        
        try {
          const freshAddressData = await getAddressData();

          if (freshAddressData) {
            // Auto-populate customer info from user's profile and skip address page
            const customerData = {
              firstName: freshAddressData.firstName || '',
              lastName: freshAddressData.lastName || '',
              businessName: freshAddressData.businessName || '',
              phone: freshAddressData.phone || '',
              email: freshAddressData.email || '',
              address1: freshAddressData.address1 || '',
              address2: freshAddressData.address2 || '',
              city: freshAddressData.city || '',
              state: freshAddressData.state || '',
              zip: freshAddressData.zipCode || '',
              userId: freshAddressData.labAccountNumber || '',
              country: 'US', // Default to US
            };

            dispatch(updateCustomer(customerData));
            history.push('/checkout/pay');
          } else {
            // If we can't get fresh address data, go to address page
            history.push('/checkout/address');
          }
        } catch (error) {
          console.warn('[Shipping] Failed to fetch fresh address data:', error);
          // If there's an error fetching fresh data, go to address page
          history.push('/checkout/address');
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      // Go to address page as usual
      history.push('/checkout/address');
    }
  };

  return (
    <Container fixed>
      <Box mt={2}>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="flex-start"
          spacing={4}
        >
          <Grid item lg={8} xs={12}>
            <GlobalOptions />
            
            {/* Shipping Options Section */}
            <OptionsSection
              title="Shipping Options"
              subtitle="Please choose a shipping option below."
              options={globalOptions.shipping}
              selectedValue={cart.shippingMethod?.id}
              onOptionChange={(shippingId) => {
                const shippingMethod = globalOptions.shipping.find(s => s.id === shippingId);
                dispatch(setShippingMethod(shippingMethod));
              }}
              isRequired={true}
              getOptionLabel={(shipping) => {
                const price = cart.price
                  ? money(
                      getShippingTotal(
                        shipping.id,
                        cart.price.lineItems,
                        cart.price.lineItemsTotal,
                        cart.price.optionsTotal,
                        globalOptions.shipping
                      ),
                      globalOptions.currency
                    )
                  : '';
                return `${shipping.shippingmethod} - ${price}`;
              }}
              getOptionDescription={(shipping) => shipping.description}
              currency={globalOptions.currency}
            />
          </Grid>
          <Grid item lg={4} xs={12}>
            <Typography variant="h6">Order Notes</Typography>
            <Typography>
              If you need to tell us something special about your order leave a
              note below.
            </Typography>
            <TextField
              fullWidth
              name="orderNotes"
              variant="outlined"
              label="Special Instructions (optional)"
              margin="normal"
              value={cart.orderNotes}
              onChange={onChangeInput}
              className={classes.textField} // Add this line
            />
            <TextField
              name="jobRef"
              variant="outlined"
              label="PO / Job Reference (optional)"
              margin="normal"
              value={cart.jobRef}
              style={{ width: 310 }}
              onChange={onChangeInput}
              className={classes.textField} // Add this line
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title="Paste PO / Job Reference from Clipboard"
                      placement="top"
                    >
                      <IconButton onClick={onPaste}>
                        <LibraryBooksIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt={2} mb={1} textAlign="center">
        <Button
          variant="outlined"
          size="medium"
          className={classes.btnxl}
          color="primary"
          component={Link}
          to="/cart"
        >
          Back
        </Button>
        <Button
          disabled={!cart.shippingMethod || isLoading}
          size="medium"
          className={classes.btnxl}
          variant="contained"
          color="secondary"
          onClick={onNext}
        >
          {isLoading ? 'Loading...' : isAuthenticated &&
          isProfileComplete() &&
          cart.shippingMethod &&
          cart.shippingMethod.shiptype !== 'dropship'
            ? 'Continue to Payment'
            : 'Continue Checkout'}
        </Button>
      </Box>
    </Container>
  );
};

export default Shipping;
